import { z } from 'zod';
import { McpServerNameSchema, TenantIdSchema } from './ids.ts';
import { ToolSourceKindSchema } from './tools/sources.ts';

export const ToolFingerprintSchema = z.string().regex(/^sha256:[0-9a-f]{64}$/);
export type ToolFingerprint = z.infer<typeof ToolFingerprintSchema>;

export const OAUTH_ERROR_CODES = [
  'invalid_request',
  'invalid_client',
  'invalid_grant',
  'unauthorized_client',
  'unsupported_grant_type',
  'invalid_scope',
] as const;
export type OAuthErrorCode = (typeof OAUTH_ERROR_CODES)[number];

export const TokenEndpointAuthMethodSchema = z.enum(['client_secret_basic', 'client_secret_post']);
export type TokenEndpointAuthMethod = z.infer<typeof TokenEndpointAuthMethodSchema>;

export const SignInClientKindSchema = z.enum(['preregistered', 'metadata_document', 'dynamic']);
export type SignInClientKind = z.infer<typeof SignInClientKindSchema>;

export const SignInRegistrationSchema = z.object({
  issuer: z.url(),
  authorizationEndpoint: z.url(),
  tokenEndpoint: z.url(),
  resource: z.url(),
  clientId: z.string().min(1).max(2048),
  clientKind: SignInClientKindSchema,
  tokenEndpointAuthMethod: z.enum(['none', 'client_secret_basic', 'client_secret_post']),
  scope: z.string().max(1024).nullable(),
  issParameterSupported: z.boolean(),
});
export type SignInRegistration = z.infer<typeof SignInRegistrationSchema>;

export const CredentialSourceConfigSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('session_token') }),
  z.object({
    type: z.literal('oauth_refresh'),
    tokenEndpoint: z.url(),
    clientId: z.string().min(1).max(256),
    tokenEndpointAuthMethod: TokenEndpointAuthMethodSchema.default('client_secret_basic'),
  }),
  z.object({ type: z.literal('user_sign_in'), registration: SignInRegistrationSchema }),
  z.object({ type: z.literal('user_api_key') }),
]);
export type CredentialSourceConfig = z.infer<typeof CredentialSourceConfigSchema>;

export const McpServerConfigSchema = z.object({
  tenantId: TenantIdSchema,
  name: McpServerNameSchema,
  kind: ToolSourceKindSchema.default('mcp'),
  title: z.string().min(1).max(64).nullable().default(null),
  url: z.url(),
  credentialSource: CredentialSourceConfigSchema.default({ type: 'session_token' }),
  enabled: z.boolean().default(true),
});
export type McpServerConfig = z.infer<typeof McpServerConfigSchema>;
export type McpServerConfigInput = z.input<typeof McpServerConfigSchema>;

export const isUserConnected = (config: Pick<McpServerConfig, 'credentialSource'>): boolean =>
  config.credentialSource.type === 'user_sign_in' ||
  config.credentialSource.type === 'user_api_key';
